import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchClaims, fetchClaimById, updateClaim } from '../services/claimsApi';

export interface Facility {
  id: string;
  location_code: string;
}
export interface LossType {
  id: string;
  name: string;
  alias: string;
}

export interface State {
  id: string;
  name: string;
  code: string;
}

export interface FacilityState {
  id: string;
  name: string;
  code: string;
}

export interface City {
  id: string;
  name: string;
  stateCode: string;
}


export interface LossType {
  id: string;
  name: string;
  code: string;
}

export interface Attachment {
  id: string;
  tpp_claim_id: string;
  user_id?: string;
  type: string;
  path: string;
  original_filename: string;
  file_size?: number;
  filesize?: number;
  token: string;
  created_at: string;
  updated_at: string;
}

export interface Claim {
  id: string;
  facility_id: string;
  facility_unit: string;
  claim_number: string;
  token?: string | null;
  status: 'SUBMITTED' | 'IN_REVIEW' | 'INCOMPLETE' | 'APPROVED' | 'DENIED' | 'DRAFT' | 'LEGAL_MEDIATION' | 'SITE_CLOSED' | 'WITH_OPERATIONS' | 'PENDING_SIGNATURE' | 'PAID' | 'CLOSED';



  reporter_first_name: string;
  reporter_last_name: string;
  owner_email: string;
  reporter_phone: string;
  owner_address_1: string;
  owner_address_2: string;
  owner_city: string;
  owner_state: string;
  owner_zip_code: string;

  tpp_loss_type_id: string;
  loss_type: LossType;
  other_loss_type_description?: string | null;
  incident_date: string;
  items_current_location: string;
  damage_discovered_by: string;
  authority_filed_report: string;
  authority_name: string;
  damage_estimate: number | string | null;
  damaged_items: string | null;
  damage_description?: string | null;
  description: string;
  info_manager_notified: boolean;


  adjuster_loss_type: LossType;
  other_loss_type: string | null;
  tpp_amount: number;
  total_paid: number;
  resolved_date: string;
  is_account_current: boolean;
  is_police_report_attached: boolean;
  is_media_submitted: boolean;
  is_authorities_involved: boolean;
  is_tpp_in_place: boolean | number;
  deny_reason: string;
  note: string;

  another_claim_filed: boolean;
  another_loss_type: LossType;
  another_claim_date: string;


  facility: Facility;
  attachments: Attachment[];

  created_at: string;
  updated_at: string;
}


interface ClaimsState {
  claims: Claim[];
  currentClaim: Claim | null;
  loading: boolean;
  error: string | null;
  fieldErrors: Record<string, string[]> | null;
  total: number;
  currentPage: number;
  perPage: number;
}

const initialState: ClaimsState = {
  claims: [],
  currentClaim: null,
  loading: false,
  error: null,
  fieldErrors: null,
  total: 0,
  currentPage: 1,
  perPage: 10,
};

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    clearCurrentClaim: (state) => {
      state.currentClaim = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all claims
      .addCase(fetchClaims.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaims.fulfilled, (state, action: PayloadAction<{ data: Claim[]; total?: number; current_page?: number; per_page?: number }>) => {
        state.loading = false;
        state.claims = action.payload.data;
        if (action.payload.total !== undefined) {
          state.total = action.payload.total;
        }
        if (action.payload.current_page !== undefined) {
          state.currentPage = action.payload.current_page;
        }
        if (action.payload.per_page !== undefined) {
          state.perPage = action.payload.per_page;
        }
      })
      .addCase(fetchClaims.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch claims';
      })
      // Fetch single claim
      .addCase(fetchClaimById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(fetchClaimById.fulfilled, (state, action: PayloadAction<Claim>) => {
        state.loading = false;
        state.currentClaim = action.payload;
      })
      .addCase(fetchClaimById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch claim';
      })
      // Update claim
      .addCase(updateClaim.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = null;
      })
      .addCase(updateClaim.fulfilled, (state, action: PayloadAction<Claim>) => {
        state.loading = false;
        state.error = null;
        state.fieldErrors = null;
        state.currentClaim = action.payload;
        const index = state.claims.findIndex(claim => claim.id === action.payload.id);
        if (index !== -1) {
          state.claims[index] = action.payload;
        }
      })
      .addCase(updateClaim.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message || 'Failed to update claim';
        state.fieldErrors = action.payload?.errors ?? null;
      });
  },
});

export const { clearCurrentClaim } = claimsSlice.actions;
export default claimsSlice.reducer; 